/**
 * Table View
 *
 * Renders Space tasks in a sortable table with safe inline edits and
 * production-backed bulk actions.
 *
 * Primary responsibilities:
 * - Show tasks in a dense table layout
 * - Persist supported edits and deletions
 *
 * Dependencies:
 * - db/space-tasks
 * - BulkEditDialog
 */

"use client"

import {
  FC,
  Fragment,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconFlag,
  IconCalendar,
  IconClock,
  IconMinus,
  IconPlus,
  IconFilter,
  IconColumns,
  IconSortAscending,
  IconSortDescending
} from "@tabler/icons-react"
import { BulkEditDialog } from "../bulk-operations/bulk-edit-dialog"
import { StatusChangeButton } from "../tasks/status-change-button"
import {
  TaskStatusIcon,
  getTaskStatusIconToneClass,
  resolveTaskStatusIconName
} from "../tasks/task-status-icon"
import { deleteTask, restoreTask, updateTask, updateTaskStatus } from "@/db/space-tasks"
import { useSpaceStatusTemplate } from "@/hooks/useSpaceStatusTemplate"
import { cn } from "@/lib/utils"
import {
  buildLosiSyntheticTaskProfile,
  isLosiTaskAssigneeId
} from "@/lib/spaces/losi-task-assignee"
import {
  formatTaskStatusLabel,
  groupTasksByStatus,
  normalizeTaskStatus
} from "@/lib/spaces/task-filters"
import { format } from "date-fns"
import { toast } from "sonner"
import { ArrowUp32, LinearDownload } from "marshmallow-icons"
import type { TaskPriority, TaskStatus } from "@/types/spaces"
import { SpaceEmptyState } from "@/components/spaces/shared/space-design"

interface Task {
  id: string
  space_id?: string
  title: string
  description?: string
  status: TaskStatus
  priority: TaskPriority
  due_date?: string
  start_date?: string
  assigned_to?: string
  assigned_to_name?: string
  assigned_to_avatar?: string
  time_estimate_minutes?: number
  time_spent_minutes?: number
  is_milestone?: boolean
  tags?: string[]
  watchers?: string[]
  folder_id?: string
  sprint_id?: string
  created_at: string
  updated_at: string
  assignee?: {
    display_name?: string | null
    username?: string | null
    email?: string | null
    image_url?: string | null
  } | null
  assignee_ids?: string[] | null
  custom_fields?: unknown
  assignees?: Array<{
    id?: string | null
    user_id?: string | null
    image_url?: string | null
    avatar_url?: string | null
    display_name?: string | null
    name?: string | null
    username?: string | null
    email?: string | null
  }> | null
}

interface TableViewProps {
  tasks: Task[]
  onTaskUpdate?: (task: Task) => void
  onTaskClick?: (task: Task) => void
  onCreateTask?: () => void
  onBulkDelete?: (taskIds: string[]) => void | Promise<void>
  spaceId: string
}

type SortField = keyof Task | 'none'
type SortDirection = 'asc' | 'desc'

const DEFAULT_STATUS_GROUP_ORDER = [
  "todo",
  "in_progress",
  "need_review",
  "blocked",
  "done",
  "cancelled"
] as const

const TITLE_EDIT_DOUBLE_CLICK_DELAY_MS = 220

const DEFAULT_COLUMNS = [
  'title',
  'status',
  'priority',
  'assigned_to_name',
  'due_date',
  'time_estimate_minutes',
  'tags'
] as const

type TableTaskAssignee = {
  key: string
  label: string
  imageUrl?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

const normalizeAssigneeId = (value: unknown) => {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null
}

const getTaskAssigneeLabel = (assignee: NonNullable<Task["assignees"]>[number]) => {
  return (
    assignee.display_name?.trim() ||
    assignee.name?.trim() ||
    assignee.username?.trim() ||
    assignee.email?.trim() ||
    null
  )
}

const getTaskAssignees = (task: Task): TableTaskAssignee[] => {
  const assigneeMap = new Map<string, TableTaskAssignee>()
  const assigneeAliasMap = new Map<string, string>()
  const orderedKeys: string[] = []

  const upsertAssignee = (input: {
    ids?: Array<string | null | undefined>
    label?: string | null
    imageUrl?: string | null
  }) => {
    const normalizedLabel =
      typeof input.label === "string" ? input.label.trim() : ""
    const normalizedIds = (input.ids || [])
      .map(candidateId => normalizeAssigneeId(candidateId))
      .filter((candidateId): candidateId is string => Boolean(candidateId))
    const shouldUseLosiProfile =
      normalizedIds.some(candidateId => isLosiTaskAssigneeId(candidateId)) ||
      (normalizedIds.length === 0 && isLosiTaskAssigneeId(normalizedLabel))
    const losiProfile = shouldUseLosiProfile
      ? buildLosiSyntheticTaskProfile()
      : null
    const displayIds = losiProfile
      ? Array.from(new Set([...normalizedIds, losiProfile.user_id || "losi"]))
      : normalizedIds
    const displayLabel = losiProfile?.display_name || normalizedLabel
    const displayImageUrl = losiProfile?.image_url || input.imageUrl || null

    if (displayIds.length === 0 && !displayLabel) {
      return
    }

    const existingKey =
      displayIds
        .map(candidateId => assigneeAliasMap.get(candidateId))
        .find((candidateKey): candidateKey is string => Boolean(candidateKey)) ||
      null
    const key =
      existingKey ||
      (losiProfile ? losiProfile.user_id || "losi" : null) ||
      displayIds[0] ||
      `label:${displayLabel.toLowerCase()}`
    const existingAssignee = assigneeMap.get(key)

    displayIds.forEach(candidateId => {
      assigneeAliasMap.set(candidateId, key)
    })

    if (!orderedKeys.includes(key)) {
      orderedKeys.push(key)
    }

    assigneeMap.set(key, {
      key,
      label: displayLabel || existingAssignee?.label || "User",
      imageUrl: displayImageUrl || existingAssignee?.imageUrl || undefined
    })
  }

  upsertAssignee({
    ids: [task.assigned_to || null],
    label:
      task.assigned_to_name ||
      task.assignee?.display_name ||
      task.assignee?.username ||
      task.assignee?.email ||
      null,
    imageUrl: task.assigned_to_avatar || task.assignee?.image_url || null
  })

  task.assignees?.forEach(assignee => {
    upsertAssignee({
      ids: [assignee.id, assignee.user_id],
      label: getTaskAssigneeLabel(assignee),
      imageUrl: assignee.image_url || assignee.avatar_url || null
    })
  })

  const assigneeIds = [
    ...(Array.isArray(task.assignee_ids) ? task.assignee_ids : []),
    ...(isRecord(task.custom_fields) && Array.isArray(task.custom_fields.assignee_ids)
      ? task.custom_fields.assignee_ids.filter(
          (candidate): candidate is string =>
            typeof candidate === "string" && candidate.trim().length > 0
        )
      : [])
  ]

  assigneeIds.forEach(assigneeId => {
    upsertAssignee({ ids: [assigneeId] })
  })

  return orderedKeys
    .map(key => assigneeMap.get(key))
    .filter((assignee): assignee is TableTaskAssignee => Boolean(assignee))
}

export const TableView: FC<TableViewProps> = ({
  tasks,
  onTaskUpdate,
  onTaskClick,
  onCreateTask,
  onBulkDelete,
  spaceId
}) => {
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set())
  const [sortField, setSortField] = useState<SortField>('title')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(DEFAULT_COLUMNS))
  const [editingCell, setEditingCell] = useState<{
    taskId: string
    field: 'title'
  } | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false)
  const [deletedTaskIds, setDeletedTaskIds] = useState<Set<string>>(new Set())
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set())
  const [priorityFilters, setPriorityFilters] = useState<Set<string>>(new Set())
  const pendingRowClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const { statusTemplate, availableStatuses } = useSpaceStatusTemplate(spaceId)

  const statusFilterOptions = useMemo(() => {
    const source = availableStatuses.length > 0
      ? availableStatuses
      : Array.from(new Set(tasks.map(task => task.status).filter(Boolean)))

    return source.filter(Boolean)
  }, [availableStatuses, tasks])

  const priorityFilterOptions = useMemo(
    () => Array.from(new Set(tasks.map(task => task.priority).filter(Boolean))),
    [tasks]
  )

  const getAssigneeLabel = useCallback(
    (task: Task) => getTaskAssignees(task)[0]?.label || '',
    []
  )

  const visibleTasks = useMemo(() => {
    return tasks.filter(task => {
      if (deletedTaskIds.has(task.id)) return false
      if (statusFilters.size > 0 && !statusFilters.has(task.status)) return false
      if (priorityFilters.size > 0 && !priorityFilters.has(task.priority)) return false
      return true
    })
  }, [deletedTaskIds, priorityFilters, statusFilters, tasks])

  const visibleTaskIds = useMemo(
    () => new Set(visibleTasks.map(task => task.id)),
    [visibleTasks]
  )

  useEffect(() => {
    setSelectedTasks(prev => {
      const next = new Set([...prev].filter(taskId => visibleTaskIds.has(taskId)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleTaskIds])

  const selectedVisibleTaskIds = useMemo(
    () => visibleTasks.filter(task => selectedTasks.has(task.id)).map(task => task.id),
    [selectedTasks, visibleTasks]
  )

  const applyLocalTaskUpdate = (
    task: Task,
    updates: Partial<Task>
  ) => {
    onTaskUpdate?.({
      ...task,
      ...updates,
      ...(Object.prototype.hasOwnProperty.call(updates, 'assigned_to')
        ? {
            assigned_to_name: updates.assigned_to === task.assigned_to
              ? task.assigned_to_name
              : updates.assigned_to_name,
            assigned_to_avatar: updates.assigned_to === task.assigned_to
              ? task.assigned_to_avatar
              : undefined,
            assignee: updates.assigned_to === task.assigned_to
              ? task.assignee
              : null
          }
        : {})
    })
  }

  const handleStatusChange = async (
    taskId: string,
    newStatus: TaskStatus
  ) => {
    const task = visibleTasks.find(item => item.id === taskId)
    if (!task) return

    try {
      await updateTaskStatus(taskId, newStatus, task.space_id || spaceId)
      applyLocalTaskUpdate(task, { status: newStatus })
    } catch (error) {
      console.error("Error updating task status:", error)
    }
  }

  // Sorting logic
  const sortedTasks = useMemo(() => {
    if (sortField === 'none') return visibleTasks

    return [...visibleTasks].sort((a, b) => {
      const aValue = a[sortField as keyof Task]
      const bValue = b[sortField as keyof Task]

      const compareNumber = (left: number, right: number) => {
        if (left < right) return -1
        if (left > right) return 1
        return 0
      }

      if (sortField === 'due_date' || sortField === 'created_at' || sortField === 'updated_at') {
        const leftTime = aValue ? new Date(String(aValue)).getTime() : Number.NEGATIVE_INFINITY
        const rightTime = bValue ? new Date(String(bValue)).getTime() : Number.NEGATIVE_INFINITY
        const comparison = compareNumber(leftTime, rightTime)
        return sortDirection === 'asc' ? comparison : -comparison
      }

      if (sortField === 'assigned_to_name') {
        const leftAssignee = getAssigneeLabel(a).toLowerCase()
        const rightAssignee = getAssigneeLabel(b).toLowerCase()
        let comparison = 0
        if (leftAssignee < rightAssignee) comparison = -1
        if (leftAssignee > rightAssignee) comparison = 1
        return sortDirection === 'asc' ? comparison : -comparison
      }

      if (sortField === 'time_estimate_minutes' || sortField === 'time_spent_minutes') {
        const leftNumber = typeof aValue === 'number' ? aValue : Number(aValue || 0)
        const rightNumber = typeof bValue === 'number' ? bValue : Number(bValue || 0)
        const comparison = compareNumber(leftNumber, rightNumber)
        return sortDirection === 'asc' ? comparison : -comparison
      }

      const aStr = String(aValue ?? '').toLowerCase()
      const bStr = String(bValue ?? '').toLowerCase()

      let comparison = 0
      if (aStr < bStr) comparison = -1
      if (aStr > bStr) comparison = 1

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [getAssigneeLabel, sortDirection, sortField, visibleTasks])

  const orderedStatusValues = useMemo(() => {
    const seenStatuses = new Set<string>()
    const orderedValues: string[] = []

    const appendStatus = (status: string | null | undefined) => {
      const normalizedStatus = normalizeTaskStatus(status)

      if (!seenStatuses.has(normalizedStatus)) {
        seenStatuses.add(normalizedStatus)
        orderedValues.push(normalizedStatus)
      }
    }

    if (statusTemplate?.statuses?.length) {
      ;[...statusTemplate.statuses]
        .sort((left, right) => left.order - right.order)
        .forEach(status => {
          appendStatus(status.statusValues?.[0] || status.id)
        })
    } else {
      DEFAULT_STATUS_GROUP_ORDER.forEach(appendStatus)
      availableStatuses.forEach(appendStatus)
      visibleTasks.forEach(task => appendStatus(task.status))
    }

    return orderedValues
  }, [availableStatuses, statusTemplate, visibleTasks])

  const groupedTasks = useMemo(
    () =>
      groupTasksByStatus(
        sortedTasks,
        orderedStatusValues,
        task => task.status,
        status => {
          const normalizedStatus = normalizeTaskStatus(status)
          const templateStatus = statusTemplate?.statuses?.find(candidate =>
            normalizeTaskStatus(candidate.statusValues?.[0] || candidate.id) ===
            normalizedStatus
          )

          return templateStatus?.name || formatTaskStatusLabel(status)
        }
      ).filter(group => group.tasks.length > 0),
    [orderedStatusValues, sortedTasks, statusTemplate]
  )

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const toggleStatusFilter = (status: string, checked: boolean) => {
    setStatusFilters(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(status)
      } else {
        next.delete(status)
      }
      return next
    })
  }

  const togglePriorityFilter = (priority: string, checked: boolean) => {
    setPriorityFilters(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(priority)
      } else {
        next.delete(priority)
      }
      return next
    })
  }

  const clearQuickFilters = () => {
    setStatusFilters(new Set())
    setPriorityFilters(new Set())
  }

  const handleSelectAll = () => {
    if (selectedVisibleTaskIds.length === visibleTasks.length) {
      setSelectedTasks(prev => {
        const next = new Set(prev)
        visibleTasks.forEach(task => next.delete(task.id))
        return next
      })
    } else {
      setSelectedTasks(prev => {
        const next = new Set(prev)
        visibleTasks.forEach(task => next.add(task.id))
        return next
      })
    }
  }

  const handleSelectTask = (taskId: string) => {
    const newSelected = new Set(selectedTasks)
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId)
    } else {
      newSelected.add(taskId)
    }
    setSelectedTasks(newSelected)
  }

  const toPersistedTaskUpdates = (updates: Partial<Task>) => {
    const persistedUpdates: Partial<
      Pick<
        Task,
        | 'title'
        | 'status'
        | 'priority'
        | 'due_date'
        | 'start_date'
        | 'assigned_to'
        | 'tags'
        | 'folder_id'
        | 'sprint_id'
      >
    > = {}

    if (updates.title !== undefined) persistedUpdates.title = updates.title
    if (updates.status !== undefined) persistedUpdates.status = updates.status
    if (updates.priority !== undefined) persistedUpdates.priority = updates.priority
    if (updates.due_date !== undefined) persistedUpdates.due_date = updates.due_date
    if (updates.start_date !== undefined) persistedUpdates.start_date = updates.start_date
    if (updates.assigned_to !== undefined) persistedUpdates.assigned_to = updates.assigned_to
    if (updates.tags !== undefined) persistedUpdates.tags = updates.tags
    if (updates.folder_id !== undefined) persistedUpdates.folder_id = updates.folder_id
    if (updates.sprint_id !== undefined) persistedUpdates.sprint_id = updates.sprint_id

    return persistedUpdates
  }

  const startEditing = (
    taskId: string,
    field: 'title',
    currentValue: string | number | null | undefined
  ) => {
    setEditingCell({ taskId, field })
    setEditValue(String(currentValue || ''))
  }

  const saveEdit = async () => {
    if (!editingCell) return

    const task = visibleTasks.find(t => t.id === editingCell.taskId)
    if (!task) return

    const updatedTask = { title: editValue.trim() }

    if (!updatedTask.title) {
      cancelEdit()
      return
    }

    try {
      await updateTask(task.id, {
        ...toPersistedTaskUpdates(updatedTask),
        space_id: task.space_id || spaceId
      })
      applyLocalTaskUpdate(task, updatedTask)
      setEditingCell(null)
      setEditValue('')
    } catch (error) {
      console.error('Error saving task edit:', error)
    }
  }

  const cancelEdit = () => {
    setEditingCell(null)
    setEditValue('')
  }

  const clearPendingRowClick = useCallback(() => {
    if (pendingRowClickTimeoutRef.current) {
      clearTimeout(pendingRowClickTimeoutRef.current)
      pendingRowClickTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      clearPendingRowClick()
    }
  }, [clearPendingRowClick])

  const openTaskFromRow = useCallback(
    (task: Task) => {
      clearPendingRowClick()
      onTaskClick?.(task)
    },
    [clearPendingRowClick, onTaskClick]
  )

  const handleRowClick = useCallback(
    (task: Task, event: MouseEvent<HTMLTableRowElement>) => {
      const target = event.target as HTMLElement
      const clickedTitleCell = Boolean(
        target.closest('[data-task-title-cell="true"]')
      )

      if (clickedTitleCell) {
        clearPendingRowClick()
        pendingRowClickTimeoutRef.current = setTimeout(() => {
          pendingRowClickTimeoutRef.current = null
          onTaskClick?.(task)
        }, TITLE_EDIT_DOUBLE_CLICK_DELAY_MS)
        return
      }

      openTaskFromRow(task)
    },
    [clearPendingRowClick, onTaskClick, openTaskFromRow]
  )

  const handleBulkUpdate = async (updates: Partial<Task>) => {
    const selectedIdsSet = new Set(selectedVisibleTaskIds)
    const tasksToUpdate = visibleTasks.filter(task => selectedIdsSet.has(task.id))
    const persistedUpdates = toPersistedTaskUpdates(updates)

    for (const task of tasksToUpdate) {
      await updateTask(task.id, {
        ...persistedUpdates,
        space_id: task.space_id || spaceId
      })
      applyLocalTaskUpdate(task, updates)
    }

    setSelectedTasks(new Set())
  }

  const handleBulkDelete = async (taskIds: string[]) => {
    if (onBulkDelete) {
      await onBulkDelete(taskIds)
    } else {
      await Promise.all(taskIds.map(taskId => deleteTask(taskId)))
      toast.success(`${taskIds.length} task${taskIds.length === 1 ? '' : 's'} moved to archive`, {
        action: {
          label: 'Undo',
          onClick: () => {
            void Promise.all(taskIds.map(taskId => restoreTask(taskId, spaceId)))
              .then(() => {
                setDeletedTaskIds((current) => {
                  const next = new Set(current)
                  taskIds.forEach(taskId => next.delete(taskId))
                  return next
                })
                toast.success(`${taskIds.length} task${taskIds.length === 1 ? '' : 's'} restored`)
              })
              .catch(() => toast.error('Failed to restore tasks'))
          }
        }
      })
    }

    setDeletedTaskIds(prev => new Set([...prev, ...taskIds]))
    setSelectedTasks(new Set())
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-500'
      case 'high': return 'text-orange-500'
      case 'medium': return 'text-yellow-500'
      case 'low': return 'text-green-500'
      default: return 'text-gray-500'
    }
  }

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <ArrowUp32 width={14} height={14} />
      case 'high': return <ArrowUp32 width={14} height={14} />
      case 'medium': return <IconMinus size={14} />
      case 'low': return <LinearDownload width={14} height={14} />
      default: return <IconMinus size={14} />
    }
  }

  const SortableHeader: FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <TableHead
      className="hover:bg-muted/50 min-w-[132px] select-none"
      aria-sort={sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="flex min-h-10 w-full touch-manipulation items-center gap-1 text-left sm:min-h-0"
        onClick={() => handleSort(field)}
      >
        {children}
        {sortField === field && (
          sortDirection === 'asc' ? <IconSortAscending size={14} /> : <IconSortDescending size={14} />
        )}
        {sortField !== field && (
          <IconSortAscending size={12} className="text-muted-foreground/50" />
        )}
      </button>
    </TableHead>
  )

  const EditableCell: FC<{
    task: Task
    field: 'title'
    value: string | number | null | undefined
    type?: 'text' | 'select' | 'date'
    options?: string[]
  }> = ({ task, field, value, type = 'text', options = [] }) => {
    const isEditing = editingCell?.taskId === task.id && editingCell?.field === field

    if (isEditing) {
      if (type === 'select') {
        return (
          <Select value={editValue} onValueChange={setEditValue}>
          <SelectTrigger className="min-h-10 sm:min-h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map(option => (
                <SelectItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      }

      return (
        <div
          data-task-title-cell="true"
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void saveEdit()
              if (e.key === 'Escape') cancelEdit()
            }}
            onBlur={() => void saveEdit()}
            className="min-h-10 sm:min-h-8"
            autoFocus
          />
        </div>
      )
    }

    return (
      <div
        data-task-title-cell="true"
        className="hover:bg-muted/30 flex min-h-10 touch-manipulation items-center rounded p-1 sm:min-h-8"
        onDoubleClick={(event) => {
          event.stopPropagation()
          clearPendingRowClick()
          startEditing(task.id, field, value)
        }}
      >
        {value || <span className="text-muted-foreground">Click to edit</span>}
      </div>
    )
  }

  const renderCell = (task: Task, column: string) => {
    switch (column) {
      case 'title':
        return (
          <div className="flex items-center gap-2">
            {task.is_milestone && (
              <div className="size-2 rounded-full bg-yellow-500" title="Milestone" />
            )}
            <EditableCell task={task} field="title" value={task.title} />
          </div>
        )

      case 'status':
        return (
          <StatusChangeButton
            currentStatus={task.status}
            onStatusChange={(newStatus) => handleStatusChange(task.id, newStatus)}
            size="sm"
            showLabel={true}
            availableStatuses={availableStatuses}
            statusTemplate={statusTemplate}
          />
        )

      case 'priority':
        return (
          <div className={`flex items-center gap-1 ${getPriorityColor(task.priority)}`}>
            {getPriorityIcon(task.priority)}
            <span className="capitalize">{task.priority}</span>
          </div>
        )

      case 'assigned_to_name': {
        const assignees = getTaskAssignees(task)
        if (assignees.length === 0) {
          return null
        }

        const visibleAssignees = assignees.slice(0, 3)
        const overflowAssigneeCount = Math.max(
          0,
          assignees.length - visibleAssignees.length
        )

        return (
          <div
            className="flex items-center gap-2"
            data-testid={`table-task-assignee-stack-${task.id}`}
            title={assignees.map(assignee => assignee.label).join(", ")}
          >
            <div className="flex -space-x-2">
              {visibleAssignees.map(assignee => (
                <Avatar
                  key={assignee.key}
                  className={cn("size-6", "border-2", "border-background")}
                  data-testid={`table-task-assignee-avatar-${task.id}`}
                >
                  <AvatarImage src={assignee.imageUrl} />
                  <AvatarFallback className="text-xs">
                    {assignee.label.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
            {overflowAssigneeCount > 0 ? (
              <Badge
                variant="secondary"
                className="h-5 rounded-full px-1.5 text-[10px]"
                data-testid={`table-task-assignee-overflow-${task.id}`}
              >
                +{overflowAssigneeCount}
              </Badge>
            ) : null}
          </div>
        )
      }

      case 'due_date':
        return task.due_date ? (
          <div className="flex items-center gap-1 text-sm">
            <IconCalendar size={14} />
            {format(new Date(task.due_date), 'MMM d, yyyy')}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">No due date</span>
        )

      case 'time_estimate_minutes':
        return task.time_estimate_minutes ? (
          <div className="flex items-center gap-1 text-sm">
            <IconClock size={14} />
            {Math.round(task.time_estimate_minutes / 60)}h
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">No estimate</span>
        )

      case 'time_spent_minutes':
        return task.time_spent_minutes ? (
          <div className="flex items-center gap-1 text-sm">
            <IconClock size={14} />
            {Math.round(task.time_spent_minutes / 60)}h
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">0h</span>
        )

      case 'tags':
        return task.tags && task.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {task.tags.slice(0, 2).map(tag => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {task.tags.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{task.tags.length - 2}
              </Badge>
            )}
          </div>
        ) : null

      case 'created_at':
        return (
          <span className="text-muted-foreground text-sm">
            {format(new Date(task.created_at), 'MMM d, yyyy')}
          </span>
        )

      default:
        return null
    }
  }

  const columnLabels: Record<string, string> = {
    title: 'Task Name',
    status: 'Status',
    priority: 'Priority',
    assigned_to_name: 'Assignee',
    due_date: 'Due Date',
    time_estimate_minutes: 'Estimate',
    time_spent_minutes: 'Time Spent',
    tags: 'Tags',
    created_at: 'Created'
  }

  const allColumns = Object.keys(columnLabels)
  const visibleColumnList = useMemo(
    () => Array.from(visibleColumns),
    [visibleColumns]
  )
  const hasQuickFilters = statusFilters.size > 0 || priorityFilters.size > 0

  useEffect(() => {
    if (sortField === 'none') return
    if (!visibleColumns.has(sortField)) {
      const fallbackSortField = visibleColumns.has('title')
        ? 'title'
        : (visibleColumnList[0] as SortField | undefined) || 'none'
      setSortField(fallbackSortField)
      if (fallbackSortField !== 'none') {
        setSortDirection('asc')
      }
    }
  }, [sortField, visibleColumns, visibleColumnList])

  const toggleColumnVisibility = (column: string) => {
    setVisibleColumns(prev => {
      const next = new Set(prev)
      if (next.has(column)) {
        if (next.size === 1) return prev
        next.delete(column)
      } else {
        next.add(column)
      }
      return next
    })
  }

  return (
    <div className="min-w-0 space-y-4">
      {/* Table Controls */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="min-h-10 touch-manipulation sm:min-h-9">
                <IconFilter size={16} className="mr-2" />
                Filter
                {(statusFilters.size > 0 || priorityFilters.size > 0) && (
                  <Badge variant="secondary" className="ml-2 h-5 rounded-full px-1.5 text-xs">
                    {statusFilters.size + priorityFilters.size}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              <div className="px-2 py-1.5 text-xs font-semibold tracking-wide">
                Status
              </div>
              {statusFilterOptions.length > 0 ? (
                statusFilterOptions.map(status => {
                  const statusIconName = resolveTaskStatusIconName({ status })

                  return (
                    <DropdownMenuCheckboxItem
                      key={`status-${status}`}
                      checked={statusFilters.has(status)}
                      onCheckedChange={(checked) => toggleStatusFilter(status, Boolean(checked))}
                      onSelect={(event) => event.preventDefault()}
                    >
                      <TaskStatusIcon
                        iconName={statusIconName}
                        className={getTaskStatusIconToneClass(statusIconName)}
                      />
                      <span>{formatTaskStatusLabel(status)}</span>
                    </DropdownMenuCheckboxItem>
                  )
                })
              ) : (
                <DropdownMenuItem disabled>No statuses</DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              <div className="px-2 py-1.5 text-xs font-semibold tracking-wide">
                Priority
              </div>
              {priorityFilterOptions.length > 0 ? (
                priorityFilterOptions.map(priority => (
                  <DropdownMenuCheckboxItem
                    key={`priority-${priority}`}
                    checked={priorityFilters.has(priority)}
                    onCheckedChange={(checked) => togglePriorityFilter(priority, Boolean(checked))}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {priority}
                  </DropdownMenuCheckboxItem>
                ))
              ) : (
                <DropdownMenuItem disabled>No priorities</DropdownMenuItem>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={statusFilters.size === 0 && priorityFilters.size === 0}
                onClick={clearQuickFilters}
              >
                Clear Filters
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="min-h-10 touch-manipulation sm:min-h-9">
                <IconColumns size={16} className="mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {allColumns.map(column => (
                <DropdownMenuItem
                  key={column}
                  onClick={() => toggleColumnVisibility(column)}
                >
                  <Checkbox 
                    checked={visibleColumns.has(column)}
                    className="mr-2"
                  />
                  {columnLabels[column]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {selectedVisibleTaskIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 lg:ml-4">
              <span className="text-muted-foreground text-sm">
                {selectedVisibleTaskIds.length} selected
              </span>
              <Button 
                variant="outline" 
                size="sm"
                className="min-h-10 touch-manipulation sm:min-h-9"
                onClick={() => setIsBulkEditOpen(true)}
              >
                <IconEdit size={14} className="mr-1" />
                Bulk Operations
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="min-h-10 touch-manipulation sm:min-h-9"
                onClick={() => setSelectedTasks(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          )}
        </div>

        <Button className="min-h-10 touch-manipulation self-start lg:self-auto" onClick={() => onCreateTask?.()}>
          <IconPlus size={16} className="mr-2" />
          Add Task
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border">
        <div className="touch-pan-x overflow-x-auto overscroll-x-contain">
        <Table className="min-w-[980px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedVisibleTaskIds.length === visibleTasks.length && visibleTasks.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              
              {visibleColumnList.map(column => (
                <SortableHeader key={column} field={column as SortField}>
                  {columnLabels[column]}
                </SortableHeader>
              ))}
              
              <TableHead className="w-12">Actions</TableHead>
            </TableRow>
          </TableHeader>
          
          <TableBody>
            {groupedTasks.map(group => {
              const groupStatusIconName = resolveTaskStatusIconName({
                status: group.status,
                title: group.label
              })

              return (
                <Fragment key={group.status}>
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={visibleColumnList.length + 2}
                      className="bg-muted/30 px-4 py-3"
                      data-testid={`task-status-group-${group.status}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <TaskStatusIcon
                            iconName={groupStatusIconName}
                            className={getTaskStatusIconToneClass(groupStatusIconName)}
                            testId={`task-status-group-icon-${group.status}`}
                          />
                          <div className="text-sm font-semibold">
                            {group.label}
                          </div>
                          <Badge variant="secondary" className="rounded-full px-2 py-0.5 text-[11px]">
                            {group.tasks.length}
                          </Badge>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {group.tasks.length === 1 ? "1 task" : `${group.tasks.length} tasks`}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                  {group.tasks.map(task => (
                    <TableRow
                      key={task.id}
                      className="hover:bg-muted/50 cursor-pointer touch-manipulation"
                      onClick={(event) => handleRowClick(task, event)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedTasks.has(task.id)}
                          onCheckedChange={() => handleSelectTask(task.id)}
                        />
                      </TableCell>

                      {visibleColumnList.map(column => (
                        <TableCell key={column}>
                          {renderCell(task, column)}
                        </TableCell>
                      ))}

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="min-h-10 touch-manipulation px-2 sm:min-h-8"
                            >
                              <IconDotsVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => openTaskFromRow(task)}>
                              <IconEdit size={14} className="mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => void handleBulkDelete([task.id])}
                            >
                              <IconTrash size={14} className="mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              )})}
          </TableBody>
        </Table>
        </div>
        
        {visibleTasks.length === 0 && (
          <div className="border-t">
            {hasQuickFilters ? (
              <div className="text-muted-foreground p-8 text-center">
                <p>No tasks match the current filters.</p>
                <Button className="mt-2" variant="outline" onClick={clearQuickFilters}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="p-4 sm:p-6">
                <button
                  type="button"
                  onClick={() => onCreateTask?.()}
                  className="group mx-auto block w-full max-w-2xl text-center transition-colors"
                >
                  <SpaceEmptyState
                    title="Create your first task"
                    description="Start capturing work directly from this list without switching views."
                    density="canvas"
                    tone="task"
                    icon={<IconPlus size={18} />}
                    className="group-hover:bg-muted/30 transition-colors"
                    action={
                      <span className="bg-foreground text-background inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-transform group-hover:scale-[1.02]">
                        Add task
                      </span>
                    }
                  />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Edit Dialog */}
      <BulkEditDialog
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        selectedTasks={selectedVisibleTaskIds
          .map(id => visibleTasks.find(task => task.id === id))
          .filter((task): task is Task => Boolean(task))}
        spaceId={spaceId}
        onBulkUpdate={handleBulkUpdate}
        onBulkDelete={handleBulkDelete}
      />
    </div>
  )
}
